import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import type { Quote } from "@fprint/shared";
import { Button, useBrand } from "@fprint/ui";

import { CustomerHeader } from "../components/CustomerHeader";
import { PriceDisclosure } from "../components/PriceDisclosure";
import { QuoteResult } from "../components/QuoteResult";
import { QuoteStatusNotice } from "../components/QuoteStatusNotice";
import { PayByCardButton, WalletFallbackStack } from "../components/OfficialPayButtons";
import { RevolutWalletButtons } from "../components/RevolutWalletButtons";
import { ReviewSubmittedNotice, SubmitForReview } from "../components/SubmitForReview";
import { useAuth } from "../lib/auth";
import { claimQuote, createOrder, createPayment, releasePayment, submitQuote } from "../lib/api";
import {
  paymentInProgressDetail,
  paymentProviderUnavailableDetail,
  reviewRequiredDetail,
  type ReviewRequiredDetail,
} from "../lib/apiError";
import { formatEuros } from "../lib/money";
import { isJobTotalPriced, manualMessage, reviewCustomerNote } from "../lib/quoteInfo";
import { clearResumeQuoteId, persistResumeQuoteId } from "../lib/resumeQuote";
import { useQuotePolling } from "../lib/useQuotePolling";

// "sandbox" | "prod" enables the Revolut checkout widget; empty/unset (CI,
// local dev) keeps the stub flow where placing the order never charges.
// Baked at build time by release.yml from the REVOLUT_ENVIRONMENT secret.
const REVOLUT_MODE = (import.meta.env.VITE_REVOLUT_MODE ?? "").trim() as
  | ""
  | "sandbox"
  | "prod";
// Merchant public key (pk_...) — required for RevolutCheckout.payments
// (Apple Pay / Google Pay / Revolut Pay). Card payWithPopup only needs the
// per-order token from POST /api/payments. Empty = official stub faces.
const REVOLUT_PUBLIC_KEY = (import.meta.env.VITE_REVOLUT_PUBLIC_KEY ?? "").trim();

/**
 * A payment that has already been captured (or a provider session already
 * created) for this quote. Once this exists we must NEVER call
 * createPayment again — retries only re-attempt order creation with the
 * SAME payment_ref. The cloud treats a duplicate payment_ref as "return the
 * already-created order as success", so retrying is safe and idempotent.
 *
 * Persisted in sessionStorage (keyed by quote id) so a reload between
 * payment capture and order creation cannot lead to a second charge.
 */
interface CapturedPayment {
  payment_id: string;
  qty: number;
}

function paymentStorageKey(quoteId: string): string {
  return `fprint:captured-payment:${quoteId}`;
}

function loadCapturedPayment(quoteId: string): CapturedPayment | null {
  try {
    const raw = sessionStorage.getItem(paymentStorageKey(quoteId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CapturedPayment>;
    if (typeof parsed.payment_id !== "string") return null;
    return {
      payment_id: parsed.payment_id,
      qty: typeof parsed.qty === "number" && parsed.qty >= 1 ? parsed.qty : 1,
    };
  } catch {
    return null;
  }
}

function saveCapturedPayment(quoteId: string, p: CapturedPayment): void {
  try {
    sessionStorage.setItem(paymentStorageKey(quoteId), JSON.stringify(p));
  } catch {
    // Storage may be unavailable (private mode); in-memory state still guards
    // against re-charging within this mount.
  }
}

function clearCapturedPayment(quoteId: string): void {
  try {
    sessionStorage.removeItem(paymentStorageKey(quoteId));
  } catch {
    // ignore
  }
}

interface PayError {
  message: string;
  /** Set when money is (or may be) captured — renders the support mailto. */
  paymentRef?: string;
  /**
   * Failed checkout *start* (no capture). Pay-by-card should release any
   * leftover reservation before creating a new session (#52 / DPAY-1).
   */
  recoverable?: boolean;
}

/**
 * Every Place-order state (pay, submitted, in_review, error, loading)
 * must keep shared chrome — otherwise a submitted Reviewed quote is a
 * dead end (no My orders / New quote). Header, not a status-panel footer.
 */
function OrderShell({
  title,
  children,
  testId,
  className = "mx-auto flex min-h-screen max-w-2xl flex-col gap-6 px-6 py-12",
}: {
  title: string;
  children: ReactNode;
  testId?: string;
  className?: string;
}) {
  return (
    <main className={className} data-testid={testId}>
      <CustomerHeader title={title} />
      {children}
    </main>
  );
}

export function OrderPlace() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const brand = useBrand();
  const { isLoaded, getToken } = useAuth();
  const tokenGetter = useCallback(() => getToken(), [getToken]);

  // Only start fetching the quote once auth has loaded (prevents calling
  // getToken before the Clerk session is ready after sign-in redirects).
  // The polling hook also covers quotes that are still slicing (pending),
  // instead of dead-ending on "Loading…" forever.
  const poll = useQuotePolling(isLoaded && quoteId ? quoteId : null, tokenGetter);

  // After Clerk **register**, claim anon ownership so My orders can list
  // this quote. Existing-account sign-in already reaches this page via
  // fallbackRedirectUrl; claim is idempotent and must not block GET/pay.
  // Best-effort — 404 means someone else already owns it.
  useEffect(() => {
    if (!isLoaded || !quoteId) return;
    persistResumeQuoteId(quoteId);
    void claimQuote(quoteId, tokenGetter).catch(() => undefined);
  }, [isLoaded, quoteId, tokenGetter]);

  const [qty, setQty] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [payError, setPayError] = useState<PayError | null>(null);
  const [capturedPayment, setCapturedPayment] = useState<CapturedPayment | null>(
    null,
  );
  // Checkout answered 409 review_required: the pay CTA flips to
  // "Submit for review"; once submitted we show the confirmation instead.
  const [reviewRequired, setReviewRequired] = useState<ReviewRequiredDetail | null>(
    null,
  );
  const [reviewSubmitted, setReviewSubmitted] = useState<Quote | null>(null);

  const payButtonRef = useRef<HTMLButtonElement>(null);

  // Restore a captured-but-unfinished payment for this quote (survives
  // reloads). If one exists, qty is locked to what was actually paid for.
  useEffect(() => {
    if (!quoteId) return;
    const saved = loadCapturedPayment(quoteId);
    if (saved) {
      setCapturedPayment(saved);
      setQty(saved.qty);
    }
  }, [quoteId]);

  // Post-login resume from the guest quote flow: SignInPage appends
  // ?autoplace=1 to the deep link. This must NEVER auto-charge — it only
  // lands the user back on the order page and pre-focuses the pay button
  // so they can confirm qty/price themselves.
  useEffect(() => {
    if (
      poll.phase === "ready" &&
      new URLSearchParams(window.location.search).get("autoplace") === "1"
    ) {
      payButtonRef.current?.focus();
    }
  }, [poll.phase]);

  // ----- Full-page states: quote not loaded / not usable yet -----

  if (poll.phase === "error") {
    return (
      <OrderShell title={brand.copy["cta.order"] ?? "Place order"}>
        <p className="text-muted-fg" role="status" data-testid="order-error">
          This quote isn&apos;t available. It may have expired, or the link is
          no longer valid.
        </p>
      </OrderShell>
    );
  }

  if (poll.phase === "failed") {
    return (
      <OrderShell title={brand.copy["cta.order"] ?? "Place order"}>
        <p className="text-danger-fg" role="alert" data-testid="order-error">
          This quote failed to slice
          {poll.quote.error ? ` (${poll.quote.error})` : ""} and can&apos;t be
          ordered.
        </p>
      </OrderShell>
    );
  }

  if (poll.phase === "timeout") {
    return (
      <OrderShell title={brand.copy["cta.order"] ?? "Place order"}>
        <p className="text-muted-fg" role="status">
          This quote is still being prepared — it&apos;s taking longer than
          usual.
        </p>
        <div className="mt-4">
          <Button variant="secondary" size="sm" onClick={poll.keepWaiting}>
            Keep waiting
          </Button>
        </div>
      </OrderShell>
    );
  }

  // Multistage terminal states that are NOT payable — never a pay button.
  if (poll.phase === "settled") {
    const settled = poll.quote;
    if (settled.status === "estimated") {
      // Machine estimate: show it clearly labelled + submit-for-review.
      // RequireAuth gates this route, so the user is signed in here.
      return (
        <OrderShell title="Submit for review" testId="order-page">
          <QuoteResult quote={settled} estimate />
          <p
            role="status"
            data-testid="quote-estimated-notice"
            className="rounded-md border border-warning bg-warning/10 px-4 py-3 text-sm text-warning-fg"
          >
            This is a machine estimate — subject to review. Submit it and our
            operator will email you the final quote before any payment.
          </p>
          <SubmitForReview quote={settled} getToken={tokenGetter} signedIn />
        </OrderShell>
      );
    }
    return (
      <OrderShell title="Your quote" testId="order-page">
        <QuoteStatusNotice quote={settled} />
      </OrderShell>
    );
  }

  if (!isLoaded || poll.phase !== "ready") {
    // idle (auth still loading) or polling (quote exists but still slicing).
    return (
      <OrderShell title={brand.copy["cta.order"] ?? "Place order"}>
        <p className="flex items-center gap-2 text-muted-fg">
          <span
            aria-hidden="true"
            className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-muted-fg border-t-transparent"
          />
          {poll.phase === "polling"
            ? "Preparing your quote — this usually takes up to a minute."
            : "Loading…"}
        </p>
      </OrderShell>
    );
  }

  const quote = poll.quote;
  const priceCents = quote.result?.price_cents;
  if (!quote.result || priceCents == null) {
    // "ready" always carries a priced result; defensive fallback.
    return (
      <OrderShell title={brand.copy["cta.order"] ?? "Place order"}>
        <p className="text-muted-fg">Loading…</p>
      </OrderShell>
    );
  }

  // Reviewed/manual quotes are priced by the operator as a JOB TOTAL for the
  // quantity locked at submit time — price_cents must NOT be multiplied by
  // qty, and the qty control is read-only. Instant quotes stay per-unit.
  const jobTotalPriced = isJobTotalPriced(quote);
  const effectiveQty = jobTotalPriced ? (quote.submitted_qty ?? qty) : qty;
  const totalCents = jobTotalPriced ? priceCents : priceCents * qty;
  const operatorNote =
    reviewCustomerNote(quote.result) ?? manualMessage(quote.result);

  async function placeOrder(paymentRef: string | null): Promise<void> {
    if (!quoteId) return;
    const order = await createOrder(
      {
        quote_id: quoteId,
        qty: effectiveQty,
        post_processing: {
          supports_removal: false,
          sanding: false,
          painting: false,
        },
        notes: null,
        payment_ref: paymentRef,
      },
      tokenGetter,
    );
    // Order exists — the captured-payment marker has served its purpose.
    clearCapturedPayment(quoteId);
    // Hard navigation ensures a clean mount of the orders page (with the
    // success banner) even after Clerk sign-in redirects or resumed flows.
    // This prevents landing on a blank/stale page in some auth timing scenarios.
    clearResumeQuoteId();
    window.location.assign(`/orders?placed=${encodeURIComponent(order.order_no)}`);
  }

  /** Money is captured (or a provider session exists): record it BEFORE
   * attempting order creation so no code path can ever charge twice. */
  function markPaymentCaptured(paymentId: string): CapturedPayment {
    const captured: CapturedPayment = { payment_id: paymentId, qty: effectiveQty };
    setCapturedPayment(captured);
    if (quoteId) saveCapturedPayment(quoteId, captured);
    return captured;
  }

  function orderCreationFailed(paymentId: string, err: Error): void {
    setPayError({
      message:
        "Your payment was received but creating the order failed. " +
        "You have NOT been charged twice — use the retry button below " +
        `(it will not charge you again), or contact support. (${err.message})`,
      paymentRef: paymentId,
    });
    setSubmitting(false);
  }

  async function handlePay(): Promise<void> {
    if (!quoteId) return;
    setSubmitting(true);
    setPayError(null);
    try {
      // A payment was already captured for this quote (this session or a
      // previous reload): ONLY retry order creation with the same
      // payment_ref. Never create a second payment. The cloud returns the
      // already-created order as success if it exists.
      if (capturedPayment) {
        try {
          await placeOrder(capturedPayment.payment_id);
        } catch (err) {
          // Route through the SAME handler the first attempt uses so the
          // "payment received, we won't charge again, contact support at
          // <mailto with ref>" message persists on repeated failures. Falling
          // to the generic catch below would set payError WITHOUT a
          // paymentRef, dropping the support mailto exactly when it's needed.
          // Money-safety is intact: this path never calls createPayment.
          orderCreationFailed(
            capturedPayment.payment_id,
            err instanceof Error ? err : new Error(String(err)),
          );
        }
        return;
      }

      if (!REVOLUT_MODE) {
        // Stub flow (CI / local dev): the cloud fabricates the payment ref;
        // nothing is charged.
        await placeOrder(null);
        return;
      }

      const session = await createPayment(
        { quote_id: quoteId, qty: effectiveQty },
        tokenGetter,
      );
      if (session.mode === "stub" || !session.token) {
        // SPA built with a Revolut mode but the cloud is still on the stub
        // provider — behave like the stub flow rather than dead-ending.
        // Persist the session ref so a retry reuses it (idempotent server-side).
        const captured = markPaymentCaptured(session.payment_id);
        try {
          await placeOrder(captured.payment_id);
        } catch (err) {
          orderCreationFailed(
            captured.payment_id,
            err instanceof Error ? err : new Error(String(err)),
          );
        }
        return;
      }

      const { default: RevolutCheckout } = await import("@revolut/checkout");
      const instance = await RevolutCheckout(session.token, REVOLUT_MODE);
      instance.payWithPopup({
        onSuccess: () => {
          // Money is captured. Persist the ref FIRST (state + sessionStorage)
          // so any retry — even after a reload — reuses it instead of
          // creating a new payment. The cloud re-verifies the ref with
          // Revolut before the order is actually created.
          const captured = markPaymentCaptured(session.payment_id);
          placeOrder(captured.payment_id).catch((err: Error) => {
            orderCreationFailed(captured.payment_id, err);
          });
        },
        onError: (err) => {
          // Charge did not complete — free the reservation so the customer can
          // retry this quote immediately instead of waiting out the TTL (DPAY-1).
          void releasePayment(quoteId, tokenGetter);
          setPayError({ message: err.message || "Payment failed" });
          setSubmitting(false);
        },
        onCancel: () => {
          void releasePayment(quoteId, tokenGetter);
          setSubmitting(false);
        },
      });
      // Keep `submitting` true while the popup is open; the callbacks above
      // own the reset.
      return;
    } catch (err) {
      // The lab's tier rules can escalate at checkout (e.g. bulk quantity):
      // a structured 409 review_required means "submit for review instead of
      // paying" — flip the CTA rather than surfacing an error.
      const review = reviewRequiredDetail(err);
      if (review) {
        setReviewRequired(review);
        setSubmitting(false);
        return;
      }
      const inProgress = paymentInProgressDetail(err);
      if (inProgress) {
        // Fresh lock from another tab (or a prior crashed start). Do NOT
        // auto-release — that would re-open the two-tab double-charge
        // (FSEC-1). Offer an explicit take-over.
        setPayError({
          message:
            inProgress.message ||
            "A payment for this quote is already in progress. " +
              "If you closed the other window, try again.",
          recoverable: true,
        });
        setSubmitting(false);
        return;
      }
      const providerDown = paymentProviderUnavailableDetail(err);
      // createPayment / RevolutCheckout threw after the lab reservation may
      // have been taken. Popup onError/onCancel already release; this catch
      // covers the #52 gap (500/502 or SDK init failure before callbacks).
      void releasePayment(quoteId, tokenGetter);
      setPayError({
        message: providerDown
          ? providerDown.message
          : err instanceof Error
            ? err.message
            : String(err),
        recoverable: providerDown ? providerDown.retryable : true,
      });
      setSubmitting(false);
    }
  }

  /** Same-user take-over of a leftover reservation, then start checkout. */
  async function handleRetryCheckout(): Promise<void> {
    if (!quoteId) return;
    setPayError(null);
    await releasePayment(quoteId, tokenGetter);
    await handlePay();
  }

  /** 409 review_required recovery: lock the currently selected qty in. */
  async function handleSubmitForReview(): Promise<void> {
    if (!quoteId) return;
    setSubmitting(true);
    setPayError(null);
    try {
      const updated = await submitQuote(quoteId, effectiveQty, tokenGetter);
      setReviewSubmitted(updated);
    } catch (err) {
      setPayError({ message: err instanceof Error ? err.message : String(err) });
    } finally {
      setSubmitting(false);
    }
  }

  const supportMailto = (ref: string): string => {
    const subject = encodeURIComponent(
      `Order problem — payment reference ${ref}`,
    );
    const body = encodeURIComponent(
      `Hi,\n\nMy payment went through but my order wasn't created.\n\n` +
        `Payment reference: ${ref}\nQuote: ${quoteId ?? "unknown"}\n\nThanks`,
    );
    return `mailto:${brand.supportEmail}?subject=${subject}&body=${body}`;
  };

  return (
    <OrderShell
      title={brand.copy["cta.order"] ?? "Place order"}
      testId="order-page"
      className="mx-auto flex min-h-screen max-w-2xl flex-col gap-8 px-6 py-12"
    >
      <section className="rounded-lg border border-border bg-bg p-6">
        <h2 className="mb-4 text-lg font-semibold text-fg">Summary</h2>
        <dl className="grid grid-cols-2 gap-3 text-sm">
          {jobTotalPriced ? (
            <>
              <dt className="text-muted-fg">Job total (reviewed)</dt>
              <dd data-testid="order-job-total">{formatEuros(priceCents)}</dd>
            </>
          ) : (
            <>
              <dt className="text-muted-fg">Unit price</dt>
              <dd data-testid="order-unit-price">{formatEuros(priceCents)}</dd>
            </>
          )}
          {quote.result.mass_g != null && (
            <>
              <dt className="text-muted-fg">Mass per part</dt>
              <dd>{quote.result.mass_g.toFixed(1)} g</dd>
            </>
          )}
        </dl>
        {operatorNote && (
          <p
            data-testid="order-operator-note"
            className="mt-4 rounded-md bg-accent px-3 py-2 text-sm"
          >
            Note from our operator: {operatorNote}
          </p>
        )}
      </section>

      <label className="flex max-w-xs flex-col gap-1 text-sm">
        <span className="font-medium">Quantity</span>
        <input
          type="number"
          min={1}
          max={50}
          value={effectiveQty}
          // Once a payment is captured, the amount is fixed — qty must not
          // drift from what was actually paid for. Reviewed/manual quotes
          // were priced for the qty locked at submit time — also read-only.
          disabled={capturedPayment !== null || jobTotalPriced}
          onChange={(e) =>
            setQty(Math.max(1, Math.min(50, Number(e.target.value) || 1)))
          }
          className="h-10 rounded-md border border-border bg-bg px-2 disabled:opacity-50"
          data-testid="order-qty"
        />
        {capturedPayment ? (
          <span className="text-xs text-muted-fg">
            Quantity is locked — your payment covered {capturedPayment.qty}{" "}
            {capturedPayment.qty === 1 ? "part" : "parts"}.
          </span>
        ) : (
          jobTotalPriced && (
            <span className="text-xs text-muted-fg">
              Quantity was locked when this job was submitted for review — the
              quoted total covers {effectiveQty}{" "}
              {effectiveQty === 1 ? "part" : "parts"}.
            </span>
          )
        )}
      </label>

      <section className="rounded-lg border border-border bg-bg p-6">
        <h2 className="mb-4 text-lg font-semibold text-fg">Payment</h2>
        {REVOLUT_MODE ? (
          <p className="mb-1 text-sm text-muted-fg">
            Payment is processed securely by Revolut — card, Revolut Pay and
            supported wallets. Your order is confirmed once payment succeeds.
          </p>
        ) : (
          <p className="mb-1 text-sm text-muted-fg">
            No card is charged yet — placing the order confirms it and we
            arrange payment with you directly before printing.
          </p>
        )}
        <p className="mb-1 text-base font-bold" data-testid="order-total">
          Total: {formatEuros(totalCents)}
        </p>
        <div className="mb-4">
          <PriceDisclosure totalCents={totalCents} />
        </div>

        {/* Payment/order errors render INLINE so the summary, quantity and
            retry button stay visible — never a full-page takeover. */}
        {payError && (
          <div
            role="alert"
            data-testid="order-error"
            className="mb-4 rounded-md border border-danger bg-danger/10 px-4 py-3 text-sm text-danger-fg"
          >
            <p>{payError.message}</p>
            {payError.recoverable && !payError.paymentRef && (
              <p className="mt-2">
                If you closed a previous payment window, use Try again —
                that cancels the leftover checkout lock.
              </p>
            )}
            {payError.paymentRef && (
              <p className="mt-2">
                Need help?{" "}
                <a
                  className="font-medium underline"
                  href={supportMailto(payError.paymentRef)}
                  data-testid="order-support-link"
                >
                  Email {brand.supportEmail}
                </a>{" "}
                and quote payment reference{" "}
                <span className="font-mono">{payError.paymentRef}</span>.
              </p>
            )}
          </div>
        )}

        {capturedPayment && !submitting && (
          <p className="mb-3 text-sm text-success-fg" role="status">
            Your payment has been received — we just need to finish creating
            the order. Retrying will not charge you again.
          </p>
        )}

        {reviewSubmitted ? (
          <ReviewSubmittedNotice qty={reviewSubmitted.submitted_qty ?? effectiveQty} />
        ) : reviewRequired ? (
          // Checkout said 409 review_required: the payment CTA is replaced by
          // submit-for-review — this job cannot be paid until an operator
          // reviews it.
          <div
            role="status"
            data-testid="order-review-required"
            className="flex flex-col gap-3 rounded-md border border-warning bg-warning/10 px-4 py-3 text-sm text-warning-fg"
          >
            <p>
              {reviewRequired.message ??
                "This job needs an operator review before payment."}
            </p>
            <Button
              size="lg"
              className="w-full"
              onClick={handleSubmitForReview}
              disabled={submitting}
              data-testid="order-submit-review"
            >
              {submitting ? "Submitting…" : "Submit for review"}
            </Button>
          </div>
        ) : (
          <div
            className="mx-auto flex w-full max-w-md flex-col gap-3"
            data-testid="order-pay-stack"
          >
            {!capturedPayment &&
              (REVOLUT_MODE && REVOLUT_PUBLIC_KEY && quoteId ? (
                <RevolutWalletButtons
                  mode={REVOLUT_MODE}
                  publicToken={REVOLUT_PUBLIC_KEY}
                  quoteId={quoteId}
                  qty={effectiveQty}
                  totalCents={totalCents}
                  getToken={tokenGetter}
                  disabled={submitting}
                  onPaid={(paymentId) => {
                    const captured = markPaymentCaptured(paymentId);
                    placeOrder(captured.payment_id).catch((err: Error) => {
                      orderCreationFailed(captured.payment_id, err);
                    });
                  }}
                  onError={(message) => {
                    void releasePayment(quoteId, tokenGetter);
                    setPayError({ message: message || "Payment failed" });
                    setSubmitting(false);
                  }}
                  onCancel={() => {
                    void releasePayment(quoteId, tokenGetter);
                    setSubmitting(false);
                  }}
                  onReviewRequired={(detail) => {
                    setReviewRequired(detail);
                    setPayError(null);
                    setSubmitting(false);
                  }}
                  onPaymentInProgress={(message) => {
                    setPayError({
                      message:
                        message ||
                        "A payment for this quote is already in progress. " +
                          "If you closed the other window, try again.",
                      recoverable: true,
                    });
                    setSubmitting(false);
                  }}
                  onBusy={setSubmitting}
                />
              ) : (
                <WalletFallbackStack />
              ))}
            <PayByCardButton
              ref={payButtonRef}
              onClick={
                payError?.recoverable && !capturedPayment
                  ? handleRetryCheckout
                  : handlePay
              }
              disabled={submitting}
              label={
                submitting
                  ? "Processing…"
                  : capturedPayment
                    ? "Retry order creation"
                    : payError?.recoverable
                      ? "Try again"
                      : `Pay by card · ${formatEuros(totalCents)}`
              }
            />
          </div>
        )}
      </section>
    </OrderShell>
  );
}
